import { useForm } from 'react-hook-form';
import { PrimaryButton, RadioButton, SecondaryButton, Typography } from '@weareopenr/component-library';
import { IPublishFormValues, PublishType, Steps } from '../../types';
import styles from './SelectPublishType.module.scss';
import { ButtonContainer } from '../../../ButtonContainer/ButtonContainer';
import { ModalTitle } from '../../../ModalTitle/ModalTitle';
import { useExternalSystems } from '../../../../hooks/useExternalSystems';
import WarningBanner from '../../../WarningBanner/WarningBanner';
import { IntegrationType } from '../../../../constants';

const ButtonLabel = ({ name, description }: { name: string; description: string }) => {
  return (
    <div className={styles['radio-button-label']}>
      <Typography weight="semi-bold">{name}</Typography>
      <Typography>{description}</Typography>
    </div>
  );
};

export const SelectPublishType = ({
  defaultValues,
  closeModal,
  submitDataToParent,
}: {
  defaultValues: IPublishFormValues;
  closeModal: () => void;
  submitDataToParent: (data: IPublishFormValues, nextStep?: number) => void;
}) => {
  const { data: externalSystems, isLoading } = useExternalSystems();
  const venues =
    externalSystems?.find(
      (externalSystem) => externalSystem.integrationType === IntegrationType.POINT_OF_SALE_MANAGEMENT,
    )?.venues || [];
  const { register, handleSubmit, watch } = useForm<Pick<IPublishFormValues, 'type'>>({
    defaultValues: {
      type: defaultValues.type,
    },
  });
  const type = watch('type');
  const onSubmit = (data: IPublishFormValues) => {
    if (data.type === PublishType.PRODUCT) {
      submitDataToParent(data, Steps.excludeProducts);
    } else {
      submitDataToParent(data);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <ModalTitle title="Publish data" subTitle="Select which data you’d like to publish." />
      {venues.length === 0 && !isLoading && (
        <div className={styles['no-venues-container']}>
          <WarningBanner
            text="To publish pricing data you need at least one venue. Create a venue to get started"
            storageKey="productPublish"
          />
        </div>
      )}
      <RadioButton
        name="type"
        value={PublishType.PRODUCT_AND_PRICING}
        register={register as any}
        disabled={venues.length === 0 || isLoading}
      >
        <ButtonLabel name="Product & pricing data" description="Publishes both product and pricing information." />
      </RadioButton>
      <RadioButton name="type" value={PublishType.PRODUCT} register={register as any}>
        <ButtonLabel
          name="Product data only"
          description="Publish product data to all venues. This includes all product details such as names and other product attributes."
        />
      </RadioButton>
      <RadioButton
        name="type"
        value={PublishType.PRICING}
        register={register as any}
        disabled={venues.length === 0 || isLoading}
      >
        <ButtonLabel
          name="Pricing data only"
          description="Publish pricing data to a selection of venues. This includes prices for selected products based on your price scheme."
        />
      </RadioButton>
      <ButtonContainer
        left={
          <SecondaryButton onClick={closeModal} size="full-width" buttonStyle="outline">
            Close
          </SecondaryButton>
        }
        right={
          <PrimaryButton
            type="submit"
            size="full-width"
            disabled={
              (venues.length === 0 && (type === PublishType.PRODUCT_AND_PRICING || type === PublishType.PRICING)) ||
              isLoading
            }
          >
            Continue
          </PrimaryButton>
        }
      />
    </form>
  );
};
